import { useEffect, useState, useCallback } from "react";
import { Mail, RefreshCw, Eye, X, Send, AlertCircle, Loader2 } from "lucide-react";
import { PageHeader } from "../components/Layout";
import { StatusBadge } from "../components/StatusBadge";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const STATUSES = ["PENDING", "SENT", "FAILED"];

function PendingBookingsPanel({ onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [edits, setEdits] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/edi/pending-bookings");
      setRows(data.rows || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (row) => {
    const bk = (edits[row.id] || "").trim();
    if (!bk) { toast.error("Enter a booking / service-order number"); return; }
    setSavingId(row.id);
    try {
      const { data } = await api.patch(`/edi/pending-bookings/${row.id}`, { booking_number: bk });
      const status = data?.edi_message?.status;
      if (status === "SENT") toast.success(`Booking saved — EDI sent to ${data.edi_message.recipient_email}`);
      else toast.warning(`Booking saved. EDI status: ${status || "unknown"}`);
      setEdits((p) => { const n = { ...p }; delete n[row.id]; return n; });
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingId(null);
    }
  };

  if (loading) return null;
  if (rows.length === 0) {
    return (
      <div className="border border-[#99D420] bg-[#F4FBE6]/60 px-4 py-3 text-sm text-gray-700" data-testid="pending-bookings-clear">
        <strong className="text-[#5BA508]">✓ No pending bookings</strong> — every gate-OUT for booking-required carriers (ARKAS) has a reference attached.
      </div>
    );
  }
  return (
    <div className="border border-[#F1471D] bg-[#FFF1ED]/70" data-testid="pending-bookings-panel">
      <div className="px-4 py-3 border-b border-[#F1471D]/40 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-[#F1471D] mt-0.5 flex-shrink-0" />
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#F1471D]">Pending bookings — {rows.length} stuck gate-OUT</div>
          <div className="text-xs text-gray-700 mt-1">
            ARKAS rejects gate-OUT EDI without a real booking / service-order reference.
            Fill in the booking for each row below and the EDI will be regenerated and sent automatically.
          </div>
        </div>
      </div>
      <table className="ps-table w-full">
        <thead>
          <tr>
            <th>Container</th><th>Carrier</th><th>Customer</th><th>Gate-OUT</th>
            <th>Last EDI error</th><th>Booking / SO #</th><th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} data-testid={`pending-${r.container_number}`}>
              <td className="font-mono font-bold">{r.container_number}</td>
              <td className="font-mono text-xs">{r.last_carrier}</td>
              <td className="text-xs">{r.customer || "—"}</td>
              <td className="text-xs">{(r.event_time || "").slice(0, 16).replace("T", " ")}</td>
              <td className="text-[11px] text-[#F1471D] max-w-[280px]">{r.edi_last_error || "—"}</td>
              <td>
                <input
                  value={edits[r.id] || ""}
                  onChange={(e) => setEdits((p) => ({ ...p, [r.id]: e.target.value.toUpperCase() }))}
                  placeholder="e.g. BIST-2606-00012"
                  className="ps-input font-mono h-8 text-xs"
                  data-testid={`pending-booking-input-${r.container_number}`}
                />
              </td>
              <td>
                <button
                  onClick={() => submit(r)}
                  disabled={savingId === r.id || !((edits[r.id] || "").trim())}
                  className="ps-btn ps-btn-primary h-8 text-xs"
                  data-testid={`pending-booking-save-${r.container_number}`}
                >
                  {savingId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                  Save & Resend
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ViewDialog({ msg, onClose }) {
  if (!msg) return null;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="edi-view-dialog">
      <div className="bg-white border border-gray-200 w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">CODECO 95B Message</div>
            <div className="font-mono font-bold text-lg">{msg.container_number}</div>
          </div>
          <button onClick={onClose} className="ps-btn ps-btn-secondary"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 overflow-y-auto space-y-3">
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div><div className="text-gray-500 uppercase tracking-widest text-[10px] font-bold">Status</div><StatusBadge status={msg.status} /></div>
            <div><div className="text-gray-500 uppercase tracking-widest text-[10px] font-bold">Recipient</div><div className="font-mono">{msg.recipient_email}</div></div>
            <div><div className="text-gray-500 uppercase tracking-widest text-[10px] font-bold">Send attempts</div><div className="font-mono">{msg.send_attempts}</div></div>
          </div>
          {msg.last_error && (
            <div className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 rounded-sm flex gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span data-testid="edi-error-text">{msg.last_error}</span>
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Raw EDI payload</div>
            <pre className="font-mono text-xs bg-[#003147] text-emerald-300 p-4 rounded-sm overflow-x-auto whitespace-pre-wrap break-all" data-testid="edi-payload">{msg.payload}</pre>
          </div>
        </div>
      </div>
    </div>
  );
}

function ResendDialog({ msg, onClose, onDone }) {
  const [recipient, setRecipient] = useState(msg?.recipient_email || "");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { setRecipient(msg?.recipient_email || ""); setErr(""); }, [msg]);
  if (!msg) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/edi/messages/${msg.id}/resend`, { recipient_email: recipient });
      if (data.status === "SENT") toast.success("Resent successfully");
      else toast.error(`Resend failed: ${data.last_error || "unknown"}`);
      onDone();
      onClose();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="edi-resend-dialog">
      <div className="bg-white border border-gray-200 w-full max-w-md">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="font-display font-extrabold text-lg">Resend EDI</div>
          <button onClick={onClose} className="ps-btn ps-btn-secondary"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="border border-gray-200 bg-gray-50 p-3 text-xs">
            <div className="font-mono font-semibold">{msg.container_number}</div>
            <div className="text-gray-500">Last status: {msg.status} · attempts: {msg.send_attempts}</div>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Recipient email</label>
            <input value={recipient} onChange={(e) => setRecipient(e.target.value)} type="email" required className="ps-input font-mono" data-testid="resend-recipient-input" />
          </div>
          {err && <div className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 rounded-sm">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="ps-btn ps-btn-secondary">Cancel</button>
            <button type="submit" disabled={submitting} className="ps-btn ps-btn-primary" data-testid="resend-submit">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Resend</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function EDIMessages() {
  const { user } = useAuth();
  const canResend = user?.role === "supervisor" || user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [view, setView] = useState(null);
  const [resend, setResend] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.append("status", statusFilter);
    try {
      const { data } = await api.get(`/edi/messages?${params.toString()}`);
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="edi-page">
      <PageHeader
        title="EDI Messages"
        subtitle="CODECO 95B transmissions, send status, and resend queue."
        testid="edi-header"
        actions={
          <button className="ps-btn ps-btn-secondary" onClick={load} data-testid="refresh-edi">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        }
      />
      <div className="p-6 sm:p-8 space-y-4">
        <PendingBookingsPanel onChanged={load} />
        <div className="border border-gray-200 bg-white p-4 flex flex-wrap items-center gap-3">
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Filter status:</span>
          <button
            onClick={() => setStatusFilter("")}
            className={`ps-btn h-8 px-3 text-xs ${statusFilter === "" ? "ps-btn-primary" : "ps-btn-secondary"}`}
            data-testid="filter-edi-all"
          >All</button>
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`ps-btn h-8 px-3 text-xs ${statusFilter === s ? "ps-btn-primary" : "ps-btn-secondary"}`}
              data-testid={`filter-edi-${s}`}
            >{s}</button>
          ))}
        </div>

        <div className="border border-gray-200 bg-white overflow-x-auto">
          <table className="ps-table" data-testid="edi-table">
            <thead>
              <tr>
                <th>Container</th>
                <th>Recipient</th>
                <th>Status</th>
                <th>Attempts</th>
                <th>Created</th>
                <th>Sent</th>
                <th>Last error</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="text-center text-gray-400 py-10">Loading…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={8} className="text-center text-gray-400 py-10">No EDI messages yet. Generate one from a gate event.</td></tr>}
              {!loading && items.map((m) => (
                <tr key={m.id} data-testid={`edi-row-${m.id}`}>
                  <td className="font-mono font-semibold">{m.container_number}</td>
                  <td className="font-mono text-gray-700">{m.recipient_email}</td>
                  <td><StatusBadge status={m.status} /></td>
                  <td className="font-mono">{m.send_attempts}</td>
                  <td className="font-mono text-gray-500">{new Date(m.created_at).toLocaleString()}</td>
                  <td className="font-mono text-gray-500">{m.sent_at ? new Date(m.sent_at).toLocaleString() : "—"}</td>
                  <td className="max-w-[280px] truncate text-xs text-red-600">{m.last_error || ""}</td>
                  <td className="text-right whitespace-nowrap">
                    <button onClick={() => setView(m)} className="ps-btn ps-btn-secondary h-8 px-3 text-xs mr-1" data-testid={`view-edi-${m.id}`}>
                      <Eye className="w-3.5 h-3.5" /> View
                    </button>
                    {canResend && (
                      <button
                        onClick={() => setResend(m)}
                        className="ps-btn ps-btn-primary h-8 px-3 text-xs"
                        data-testid={`resend-edi-${m.id}`}
                      >
                        <Mail className="w-3.5 h-3.5" /> Resend
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ViewDialog msg={view} onClose={() => setView(null)} />
      <ResendDialog msg={resend} onClose={() => setResend(null)} onDone={load} />
    </div>
  );
}
